import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Mail, Lock, User, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth, startGoogleLogin } from "../lib/auth";
import { api } from "../lib/apiClient";
import { formatApiError } from "../lib/errors";

const input = "w-full border border-slate-200 rounded-lg px-10 py-2.5 text-sm focus:border-[#0EA5E9] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/20 bg-white";
const label = "text-xs font-bold text-[#0B1B3D] font-display";

export default function AuthDialog({ open, onClose, initialMode = "signin" }) {
  const [mode, setMode] = useState(initialMode); // "signin" | "signup" | "forgot"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { loginWithPassword, registerWithPassword } = useAuth();
  const navigate = useNavigate();

  if (!open) return null;

  const close = () => {
    setEmail(""); setPassword(""); setName(""); setBusy(false); setMode(initialMode);
    onClose?.();
  };

  const onSignIn = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await loginWithPassword(email.trim(), password);
      toast.success("Welcome back");
      close();
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err, "Could not sign in"));
    } finally { setBusy(false); }
  };

  const onSignUp = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setBusy(true);
    try {
      await registerWithPassword(email.trim(), password, name.trim() || null);
      toast.success("Account created");
      close();
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err, "Could not create account"));
    } finally { setBusy(false); }
  };

  const onForgot = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim(), frontend_origin: window.location.origin });
      toast.success("If an account exists, we've sent a reset link.");
      setMode("signin");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-[#0B1B3D]/40 backdrop-blur-sm" data-testid="auth-dialog">
      <div className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-[#0B1B3D] grid place-items-center"><span className="font-display font-black text-white text-sm">K</span></div>
            <p className="font-display font-extrabold text-[#0B1B3D]">
              {mode === "signin" && "Sign in to Konzenzo"}
              {mode === "signup" && "Create your account"}
              {mode === "forgot" && "Reset your password"}
            </p>
          </div>
          <button data-testid="auth-close-btn" onClick={close} className="text-[#94A3B8] hover:text-[#0B1B3D]"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          {mode !== "forgot" && (
            <>
              <button
                type="button"
                data-testid="google-signin-btn"
                onClick={startGoogleLogin}
                className="w-full flex items-center justify-center gap-3 border border-slate-200 rounded-lg px-4 py-3 font-bold text-sm text-[#0B1B3D] hover:border-[#0B1B3D] hover:bg-slate-50 transition-colors font-display"
              >
                <svg className="w-4 h-4" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.8 32.4 29.3 35.5 24 35.5c-6.3 0-11.5-5.2-11.5-11.5S17.7 12.5 24 12.5c2.9 0 5.6 1.1 7.7 2.9l5.7-5.7C33.9 6.5 29.2 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5c10.8 0 19.5-8.7 19.5-19.5 0-1.1-.1-2.2-.4-3.5z"/>
                  <path fill="#FF3D00" d="M6.3 14.1l6.6 4.8C14.6 15.2 18.9 12.5 24 12.5c2.9 0 5.6 1.1 7.7 2.9l5.7-5.7C33.9 6.5 29.2 4.5 24 4.5 16.3 4.5 9.7 8.9 6.3 14.1z"/>
                  <path fill="#4CAF50" d="M24 43.5c5.1 0 9.7-1.9 13.1-5.1l-6-5.1c-1.9 1.4-4.3 2.2-7.1 2.2-5.3 0-9.8-3.1-11.3-7.6l-6.5 5C9.7 39.1 16.3 43.5 24 43.5z"/>
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2.1-2.1 3.9-3.9 5.2l6 5.1c4.3-3.9 6.6-9.6 6.6-15.8 0-1.1-.1-2.2-.4-3.5z"/>
                </svg>
                Continue with Google
              </button>
              <div className="flex items-center gap-3 text-xs text-[#94A3B8] font-mono-kz tracking-widest">
                <div className="flex-1 h-px bg-slate-200" />
                OR
                <div className="flex-1 h-px bg-slate-200" />
              </div>
            </>
          )}

          {mode === "signin" && (
            <form onSubmit={onSignIn} className="space-y-3" data-testid="signin-form">
              <FieldIcon label="Email" icon={Mail}>
                <input data-testid="signin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={input} placeholder="you@company.com" />
              </FieldIcon>
              <FieldIcon label="Password" icon={Lock}>
                <input data-testid="signin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={input} placeholder="••••••••" />
              </FieldIcon>
              <div className="flex items-center justify-between">
                <button type="button" data-testid="forgot-link" onClick={() => setMode("forgot")} className="text-xs font-bold text-[#0EA5E9] hover:underline">Forgot password?</button>
              </div>
              <button data-testid="signin-submit" type="submit" disabled={busy} className="w-full bg-[#0B1B3D] disabled:opacity-50 text-white hover:bg-[#1E3A8A] px-5 py-3 rounded-lg font-bold text-sm font-display inline-flex items-center justify-center gap-2">{busy ? "Signing in…" : "Sign in"} <ArrowRight className="w-4 h-4" /></button>
              <p className="text-center text-sm text-[#475569]">
                New to Konzenzo?{" "}
                <button type="button" data-testid="switch-to-signup" onClick={() => setMode("signup")} className="font-bold text-[#0B1B3D] hover:underline">Create an account</button>
              </p>
            </form>
          )}

          {mode === "signup" && (
            <form onSubmit={onSignUp} className="space-y-3" data-testid="signup-form">
              <FieldIcon label="Your name" icon={User}>
                <input data-testid="signup-name" value={name} onChange={(e) => setName(e.target.value)} className={input} placeholder="Ana Müller" />
              </FieldIcon>
              <FieldIcon label="Work email" icon={Mail}>
                <input data-testid="signup-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={input} placeholder="you@company.com" />
              </FieldIcon>
              <FieldIcon label="Password (min 8 chars)" icon={Lock}>
                <input data-testid="signup-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className={input} placeholder="••••••••" />
              </FieldIcon>
              <button data-testid="signup-submit" type="submit" disabled={busy} className="w-full bg-[#10B981] disabled:opacity-50 text-white hover:bg-[#059669] px-5 py-3 rounded-lg font-bold text-sm font-display inline-flex items-center justify-center gap-2">{busy ? "Creating…" : "Create account"} <ArrowRight className="w-4 h-4" /></button>
              <div className="flex items-start gap-2 text-xs text-[#475569]"><ShieldCheck className="w-4 h-4 text-[#10B981] mt-0.5 shrink-0" /><p>By creating an account, you agree that Konzenzo provides AI analysis to support — not replace — qualified human review for tax, legal and financial matters.</p></div>
              <p className="text-center text-sm text-[#475569]">
                Already have an account?{" "}
                <button type="button" data-testid="switch-to-signin" onClick={() => setMode("signin")} className="font-bold text-[#0B1B3D] hover:underline">Sign in</button>
              </p>
            </form>
          )}

          {mode === "forgot" && (
            <form onSubmit={onForgot} className="space-y-3" data-testid="forgot-form">
              <p className="text-sm text-[#475569]">Enter your email and we'll send you a link to reset your password.</p>
              <FieldIcon label="Email" icon={Mail}>
                <input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={input} placeholder="you@company.com" />
              </FieldIcon>
              <button data-testid="forgot-submit" type="submit" disabled={busy} className="w-full bg-[#0B1B3D] disabled:opacity-50 text-white hover:bg-[#1E3A8A] px-5 py-3 rounded-lg font-bold text-sm font-display">{busy ? "Sending…" : "Send reset link"}</button>
              <p className="text-center text-sm">
                <button type="button" data-testid="back-to-signin" onClick={() => setMode("signin")} className="font-bold text-[#0B1B3D] hover:underline">Back to sign in</button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

const FieldIcon = ({ label: lbl, icon: Icon, children }) => (
  <div>
    <label className={label}>{lbl}</label>
    <div className="relative mt-1">
      <Icon className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
      {children}
    </div>
  </div>
);
